import { useLocale } from "@/hooks/useLocale";
import { displayRunName } from "@/utils/payroll-names";
import { useNavigate, useParams } from "react-router-dom";
import { CancelRunDialog } from "./_components/CancelRunDialog";
import { warnIfIbanChecksumFails } from "@/utils/payrollIbanWarning";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAppDispatch } from "@/hooks/useReduxHooks";
import { format } from "date-fns";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  FileText,
  Loader2,
  MoreHorizontal,
  XCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { useConfirm } from "@/context/Confirm";

import { usePayrollRunSocket } from "@/services/websocket/usePayrollRunSocket";

import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { ROUTES } from "@/constants/routes";
import { usePageTitle } from "@/hooks/usePageTitle";
import { withBranding } from "@/utils/helper";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import {
  ProgressStepper,
  type ProgressStep,
  type StepStatus,
} from "@/components/ProgressStepper";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RunGenerationProgress } from "../generate-payroll/_components/RunGenerationProgress";
import { SummaryAccordions } from "./_components/SummaryAccordions";
import { EarningsTable } from "./_components/EarningsTable";
import { PaymentFilesCard } from "./_components/PaymentFilesCard";
import { CashDisbursementCard } from "./_components/CashDisbursementCard";
import { BankTransferCard } from "./_components/BankTransferCard";
import { WpsExcludedTab } from "./_components/WpsExcludedTab";
import {
  companyPayrollRunsApi,
  useGetRunDetailQuery,
  useGetPaymentFilesQuery,
  useGetCashDisbursementsQuery,
  useGetBankTransfersQuery,
  useGetWpsExcludedQuery,
  useListItemsQuery,
  useStartReviewMutation,
  useApproveRunMutation,
  useDeleteRunMutation,
  useRetryFailedMutation,
  useProcessRunMutation,
  type IPayrollRunItem,
} from "@/services/(company)/payrollRunsApi";
import { useGetCompanyOvertimeRequestsQuery } from "@/services/(company)/api.overtime";
import type { IOvertimeRequest } from "@/types/overtime";
import { PayrollRunStatusEnum } from "@/utils/enums/payroll.enum";
import { Trans, useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

type Stage = "calculated" | "in-review" | "approved" | "processed";

const STATUS_TO_STAGE: Partial<Record<PayrollRunStatusEnum, Stage>> = {
  [PayrollRunStatusEnum.GENERATING]: "calculated",
  [PayrollRunStatusEnum.DRAFT]: "calculated",
  [PayrollRunStatusEnum.IN_REVIEW]: "in-review",
  [PayrollRunStatusEnum.APPROVED]: "approved",
  [PayrollRunStatusEnum.PROCESSING]: "approved",
  [PayrollRunStatusEnum.COMPLETED]: "processed",
};

const STAGE_TO_STEP_INDEX: Record<Stage, number> = {
  calculated: 1,
  "in-review": 2,
  approved: 3,
  processed: 4,
};

const buildStepLabels = (
  t: TFunction,
): { label: string; subLabel: string }[] => [
  {
    label: t("company:payrolls.scope_set"),
    subLabel: t("company:payrolls.employees_defined"),
  },
  {
    label: t("company:payrolls.calculate"),
    subLabel: t("company:payrolls.earnings_computed"),
  },
  {
    label: t("company:payrolls.review"),
    subLabel: t("company:payrolls.adjust_and_approve"),
  },
  {
    label: t("company:payrolls.approval"),
    subLabel: t("company:payrolls.ready_to_process"),
  },
  {
    label: t("company:payrolls.process"),
    subLabel: t("company:payrolls.files_generated"),
  },
];

// Short human label for each WPS exclusion reason, for the avatar hover card.
const buildWpsExclusionLabels = (t: TFunction): Record<string, string> => ({
  EXCLUDED_STATUS: t("company:payrolls.excluded_from_run"),
  ON_HOLD: t("company:payrolls.salary_on_hold"),
  MISSING_PERSON_ID: t("company:payrolls.missing_wps_id"),
  MISSING_ROUTING: t("company:payrolls.missing_routing_code"),
  MISSING_IBAN: t("company:payrolls.missing_iban"),
  INVALID_IBAN: t("company:payrolls.invalid_iban"),
  MISSING_BANK_NAME: t("company:payrolls.missing_bank_name"),
  ZERO_NET_PAY: t("company:payrolls.zero_negative_net_pay"),
});

// Popover that opens on hover (Radix has no hover-card here). Keeps the
// portaled popover look — no layout shift — with a small close delay so the
// pointer can travel from trigger into the content without it snapping shut.
const HoverPopover = ({
  trigger,
  align = "start",
  contentClassName,
  children,
}: {
  trigger: ReactNode;
  align?: "start" | "center" | "end";
  contentClassName?: string;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
  };
  const show = () => {
    cancel();
    setOpen(true);
  };
  const hide = () => {
    cancel();
    timer.current = setTimeout(() => setOpen(false), 120);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild onMouseEnter={show} onMouseLeave={hide}>
        {trigger}
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          align={align}
          onMouseEnter={show}
          onMouseLeave={hide}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={contentClassName}
        >
          {children}
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};

const getExcludedInitials = (name: string | null): string =>
  (name ?? "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "?";

const buildSteps = (stage: Stage, t: TFunction): ProgressStep[] => {
  const currentIdx = STAGE_TO_STEP_INDEX[stage];
  const isFinal = stage === "processed";
  return buildStepLabels(t).map((s, i) => {
    let status: StepStatus;
    if (isFinal) status = "done";
    else if (i < currentIdx) status = "done";
    else if (i === currentIdx) status = "current";
    else status = "future";
    return { number: i + 1, ...s, status };
  });
};

const buildStageTableSubtitle = (t: TFunction): Record<Stage, string> => ({
  calculated: t(
    "company:payrolls.ot_auto_detected_edit_hours_bonuses_and_deductions_below",
  ),
  "in-review": t("company:payrolls.readonly_in_review"),
  approved: t("company:payrolls.readonly_approved"),
  processed: t("company:payrolls.readonly_processed"),
});

interface StageActions {
  primaryLabel: string;
  primaryDisabled?: boolean;
  backLabel: string;
  ctaTitle: string;
  ctaDescription: string;
}

const buildStageActions = (t: TFunction): Record<Stage, StageActions> => ({
  calculated: {
    primaryLabel: t("company:payrolls.start_review"),
    backLabel: t("common:buttons.back"),
    ctaTitle: t("company:payrolls.ready_to_start_review"),
    ctaDescription: t(
      "company:payrolls.earnings_are_locked_once_review_starts_ot_approval_workf",
    ),
  },
  "in-review": {
    primaryLabel: t("company:payrolls.approve_payroll"),
    backLabel: t("common:buttons.back"),
    ctaTitle: t("company:payrolls.all_reviewed_approve_payroll"),
    ctaDescription: t(
      "company:payrolls.click_approve_to_lock_and_proceed_to_processing",
    ),
  },
  approved: {
    primaryLabel: t("company:payrolls.process_and_generate_files"),
    backLabel: t("common:buttons.back"),
    ctaTitle: t("company:payrolls.ready_to_process"),
    ctaDescription: t(
      "company:payrolls.click_process_to_generate_payslips_and_payment_files",
    ),
  },
  processed: {
    primaryLabel: t("company:payrolls.processed"),
    primaryDisabled: true,
    backLabel: t("company:payrolls.exit"),
    ctaTitle: "",
    ctaDescription: "",
  },
});

const Header = ({
  title,
  breadcrumb,
  onBack,
  actions,
  onPrimary,
  isWorking,
}: {
  title: string;
  breadcrumb: string;
  onBack: () => void;
  actions: StageActions;
  onPrimary: () => void;
  isWorking: boolean;
}) => {
  const { t } = useTranslation(["company", "common"]);
  return (
    <div className="flex items-center justify-between gap-3 rounded-xs border border-border bg-white px-5 py-4">
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onBack}
          className="border border-surface-elevated rounded-xs p-2 w-fit h-fit shrink-0"
        >
          <ChevronLeft className="text-primary size-5 rtl:rotate-180" />
        </button>
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-semibold text-gray-900">{title}</span>
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <span>{t("company:payrolls.home")}</span>
            <span>/</span>
            <MoreHorizontal size={12} />
            <span>/</span>
            <span>{t("company:payrolls.breadcrumb_payroll")}</span>
            <span>/</span>
            <span className="text-gray-700 font-medium truncate">
              {breadcrumb}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button variant="outline" onClick={onBack}>
          {actions.backLabel}
        </Button>
        <PermissionButton
          section={PLATFORM_SECTION_ENUM.PAYROLL}
          action="edit"
          mode="disable"
          variant="primary"
          onClick={onPrimary}
          disabled={actions.primaryDisabled || isWorking}
        >
          {isWorking ? t("common:states.working") : actions.primaryLabel}
        </PermissionButton>
      </div>
    </div>
  );
};

const ProcessLiveCard = ({
  started,
  files,
  isComplete,
}: {
  started: ReturnType<typeof usePayrollRunSocket>["processStarted"];
  files: ReturnType<typeof usePayrollRunSocket>["processFiles"];
  isComplete: boolean;
}) => {
  const { t } = useTranslation(["company", "common"]);
  const total = started?.totalFiles ?? files.length;
  const doneCount = files.filter((f) => f.status === "DONE").length;
  const percent = total > 0 ? Math.round((doneCount / total) * 100) : 0;

  return (
    <div className="rounded-xs border border-border bg-white px-5 py-4 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        {isComplete ? (
          <CheckCircle2 className="size-5 text-emerald-500" />
        ) : (
          <Loader2 className="size-5 text-primary animate-spin" />
        )}
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-gray-900">
            {isComplete
              ? t("company:payrolls.payment_files_ready")
              : t("company:payrolls.generating_payment_files")}
          </span>
          <span className="text-xs text-gray-500">
            {started?.cashItemsCount
              ? t("company:payrolls.files_ready_progress_with_cash", {
                  done: doneCount,
                  total: total || "—",
                  cash: started.cashItemsCount,
                })
              : t("company:payrolls.files_ready_progress", {
                  done: doneCount,
                  total: total || "—",
                })}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{t("common:shared_ui.progress")}</span>
          <span className="font-semibold text-gray-700">{percent}%</span>
        </div>
        <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full bg-primary transition-[width] duration-300"
            style={{ width: `${Math.min(100, percent)}%` }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2 max-h-64 overflow-auto">
        {files.length === 0 ? (
          <div className="text-xs text-gray-400 py-3 text-center">
            {t("company:payrolls.waiting_for_first_file")}
          </div>
        ) : (
          files
            .slice()
            .sort((a, b) => a.fileIndex - b.fileIndex)
            .map((f) => (
              <div
                key={f.fileIndex}
                className="flex items-center gap-3 rounded-xs border border-border bg-white px-3 py-2"
              >
                {f.status === "DONE" ? (
                  <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
                ) : f.status === "UPLOADING" ? (
                  <Loader2 className="size-4 text-primary animate-spin shrink-0" />
                ) : (
                  <FileText className="size-4 text-gray-400 shrink-0" />
                )}
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-sm font-semibold text-gray-900 truncate">
                    {f.fileName}
                  </span>
                  <span className="text-[11px] text-gray-400 truncate">
                    {[
                      f.bankName,
                      f.employeeCount ? `${f.employeeCount} emp` : null,
                      Number.isFinite(Number(f.totalAmount))
                        ? `${f.currency ? `${f.currency} ` : ""}${Number(
                            f.totalAmount,
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
                <span
                  className={
                    f.status === "DONE"
                      ? "text-[10px] font-semibold uppercase text-emerald-600"
                      : f.status === "UPLOADING"
                        ? "text-[10px] font-semibold uppercase text-primary"
                        : "text-[10px] font-semibold uppercase text-gray-400"
                  }
                >
                  {f.status}
                </span>
                {f.fileUrl && (
                  <a
                    href={f.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] font-semibold text-primary hover:underline"
                  >
                    {t("common:buttons.download")}
                  </a>
                )}
              </div>
            ))
        )}
      </div>
    </div>
  );
};

const ProcessFailedBanner = ({
  error,
  onRetry,
  isRetrying,
}: {
  error: string;
  onRetry?: () => void;
  isRetrying?: boolean;
}) => {
  const { t } = useTranslation(["company", "common"]);
  return (
    <div className="flex items-center justify-between gap-3 rounded-xs border border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-600">
      <div className="flex items-center gap-2 min-w-0">
        <XCircle className="size-4 shrink-0" />
        <span className="truncate">{error}</span>
      </div>
      {onRetry && (
        <PermissionButton
          section={PLATFORM_SECTION_ENUM.PAYROLL}
          action="edit"
          mode="hide"
          type="button"
          variant="outline"
          size="sm"
          onClick={onRetry}
          disabled={isRetrying}
          className="border-red-300 text-red-600 hover:bg-red-100 shrink-0"
        >
          {isRetrying
            ? t("company:payrolls.retrying")
            : t("common:buttons.retry")}
        </PermissionButton>
      )}
    </div>
  );
};

export const PayrollRun = () => {
  const { t } = useTranslation(["company", "common"]);
  const { formatDate: localeDate, formatNumber: localeNumber } = useLocale();
  usePageTitle(withBranding(t("company:payrolls.payroll_run")));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const runId = Number(id);

  useEffect(() => {
    if (!runId || isNaN(runId)) return;
    dispatch(
      companyPayrollRunsApi.util.invalidateTags([
        { type: "payrollRunItems", id: runId },
      ]),
    );
  }, [runId, dispatch]);

  const { data: runResponse, refetch: refetchRun } = useGetRunDetailQuery(
    runId,
    {
      skip: !runId || isNaN(runId),
    },
  );
  const run = runResponse?.data;

  const stageFromStatus: Stage = run?.status
    ? (STATUS_TO_STAGE[run.status] ?? "calculated")
    : "calculated";

  const {
    data: paymentFilesResponse,
    isLoading: isLoadingPaymentFiles,
    refetch: refetchPaymentFiles,
  } = useGetPaymentFilesQuery(runId, {
    skip: !runId || isNaN(runId) || stageFromStatus !== "processed",
  });
  const paymentFiles = paymentFilesResponse?.data?.data ?? [];

  const {
    data: cashDisbursementsResponse,
    isLoading: isLoadingCashDisbursements,
  } = useGetCashDisbursementsQuery(runId, {
    skip: !runId || isNaN(runId) || stageFromStatus !== "processed",
  });
  const cashDisbursements = cashDisbursementsResponse?.data?.data ?? [];

  const { data: bankTransfersResponse, isLoading: isLoadingBankTransfers } =
    useGetBankTransfersQuery(runId, {
      skip: !runId || isNaN(runId) || stageFromStatus !== "processed",
    });
  const bankTransfers = bankTransfersResponse?.data?.data ?? [];

  const {
    progress: genProgress,
    items: genItems,
    isGenerationComplete,
    processStarted,
    processFiles,
    processCompleted,
    processFailed,
    signatureStarted,
    signatureCompleted,
    signatureFailed,
  } = usePayrollRunSocket(runId && !isNaN(runId) ? runId : null);

  const isGenerating = run?.status === PayrollRunStatusEnum.GENERATING;

  // Tab counts derived from the run's payment-method breakdown (no extra
  // request). On Hold lists held; Active lists the rest minus WPS-blocked.
  const paymentBreakdown = run?.paymentMethodBreakdown ?? [];
  const totalItemsCount = paymentBreakdown.reduce(
    (sum, b) => sum + (b.employeeCount ?? 0),
    0,
  );
  const heldItemsCount = paymentBreakdown.reduce(
    (sum, b) => sum + (b.heldCount ?? 0),
    0,
  );

  // WPS employees excluded from the SIF file (missing bank data, zero net, etc).
  const { data: wpsExcludedResponse } = useGetWpsExcludedQuery(
    runId && !isNaN(runId) ? runId : (undefined as unknown as number),
    { skip: !runId || isNaN(runId), refetchOnMountOrArgChange: true },
  );
  const wpsExcludedCount = wpsExcludedResponse?.data?.total ?? 0;
  const wpsExcludedItems = wpsExcludedResponse?.data?.items ?? [];
  // Only these stop approval (B33); zero net pay and an explicit exclusion
  // are listed for review but do not need clearing.
  const wpsBlockingCount =
    wpsExcludedResponse?.data?.blockingCount ?? wpsExcludedCount;

  // Controlled so the banner's "View Excluded" button can jump to the tab.
  const [activeTab, setActiveTab] = useState("active");

  // Fixable WPS-blocked items (missing bank / negative net) — hidden from the
  // Active tab until resolved. Legit zero-net stays; on-hold lives in On Hold.
  const blockingItemIds = (wpsExcludedResponse?.data?.items ?? [])
    .filter((i) => {
      if (i.reason === "EXCLUDED_STATUS") return false;
      // Net ≤ 0 blocks every method; otherwise missing bank by method.
      if (i.netPay <= 0) return true;
      if (i.paymentMethod === "WPS")
        return !i.hasPersonId || !i.hasRouting || !i.hasIban;
      if (i.paymentMethod === "BANK_TRANSFER")
        return !i.hasIban || !i.hasBankName;
      return false; // cash / cheque with net > 0
    })
    .map((i) => i.itemId);
  const activeItemsCount = Math.max(
    0,
    totalItemsCount - heldItemsCount - blockingItemIds.length,
  );

  // Approved (Time Attendance) overtime for this run's period, cross-checked
  // against each item's overtimePay to flag OT that never made it into payroll
  // (HM-1095). Purely a FE reconciliation — overtimePay is derived server-side
  // from OVERTIME-sourced earning lines, so no backend read change is needed.
  const { data: approvedOtResponse } = useGetCompanyOvertimeRequestsQuery(
    {
      status: "APPROVED",
      branchId: run?.branchId,
      dateFrom: run?.periodStart,
      dateTo: run?.periodEnd,
      limit: 300,
    },
    { skip: !run },
  );
  const approvedOtByUser = useMemo(() => {
    const map = new Map<number, IOvertimeRequest[]>();
    for (const req of approvedOtResponse?.data ?? []) {
      const list = map.get(req.userId);
      if (list) list.push(req);
      else map.set(req.userId, [req]);
    }
    return map;
  }, [approvedOtResponse]);

  // Every active (non-held) item, uncapped by the visible table's own 20/page
  // window — the banner and header badge need the whole run, not one page.
  // 100 is the API's page-size ceiling (PaginationDto `@Max(100)`): asking for
  // 500 was a 400 on every load, so the overtime banner never showed at all.
  // A run with more than 100 active employees is checked on its first 100.
  const { data: allActiveItemsResponse } = useListItemsQuery(
    { runId, page: 1, limit: 100, holdSalary: false },
    { skip: !runId || isNaN(runId) },
  );
  const otMismatchedItems = useMemo(() => {
    const items: IPayrollRunItem[] = allActiveItemsResponse?.data ?? [];
    return items.filter((item) => {
      const pay = Number(item.overtimePay);
      const isZero = !pay || !Number.isFinite(pay);
      return isZero && (approvedOtByUser.get(item.userId)?.length ?? 0) > 0;
    });
  }, [allActiveItemsResponse, approvedOtByUser]);
  const otSyncStatus: "synced" | "pending" | undefined = allActiveItemsResponse
    ? otMismatchedItems.length > 0
      ? "pending"
      : "synced"
    : undefined;

  const [otBannerDismissed, setOtBannerDismissed] = useState(false);
  useEffect(() => {
    setOtBannerDismissed(false);
  }, [runId]);

  useEffect(() => {
    if (!isGenerating) return;
    if (!isGenerationComplete) return;
    refetchRun();
  }, [isGenerating, isGenerationComplete, refetchRun]);

  const seenCompletedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!processCompleted) return;
    const key = `${processCompleted.runId}:${processCompleted.status}`;
    if (seenCompletedRef.current === key) return;
    seenCompletedRef.current = key;
    toast.success(
      processCompleted.status === "COMPLETED"
        ? t("company:payrolls.payroll_completed")
        : t("company:payrolls.payment_files_generated"),
    );
    refetchRun();
    if (paymentFilesResponse) refetchPaymentFiles();
  }, [processCompleted, refetchRun, refetchPaymentFiles, paymentFilesResponse]);

  const seenFailedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!processFailed) return;
    const key = `${processFailed.runId}:${processFailed.error}`;
    if (seenFailedRef.current === key) return;
    seenFailedRef.current = key;
    toast.error(processFailed.error || t("company:payrolls.processing_failed"));
    refetchRun();
  }, [processFailed, refetchRun]);

  const [startReview, { isLoading: isStartingReview }] =
    useStartReviewMutation();
  const [approveRun, { isLoading: isApproving }] = useApproveRunMutation();
  const [processRun, { isLoading: isProcessing }] = useProcessRunMutation();

  const isWorking = isStartingReview || isApproving || isProcessing;

  const handleRetryProcess = useCallback(() => {
    if (!runId || isNaN(runId)) return;
    toast.promise(
      processRun(runId)
        .unwrap()
        .then((res) => warnIfIbanChecksumFails(res, t)),
      {
        loading: t("company:payrolls.retrying_payroll_processing"),
        success: t("company:payrolls.retry_started"),
        error: (err) => {
          const msg = err?.data?.message;
          return Array.isArray(msg)
            ? msg.join(", ")
            : (msg ?? err?.message ?? t("company:payrolls.retry_failed"));
        },
      },
    );
  }, [runId, processRun, t]);

  const stage: Stage = stageFromStatus;

  const handleBack = () => navigate(ROUTES.HOME.COMPANY.PAYROLL.ROOT);

  // A run can be deleted until it is approved; after that it is history.
  const confirm = useConfirm();
  const [deleteRun, { isLoading: isDeleting }] = useDeleteRunMutation();
  const canDeleteRun =
    run?.status === PayrollRunStatusEnum.DRAFT ||
    run?.status === PayrollRunStatusEnum.IN_REVIEW ||
    run?.status === PayrollRunStatusEnum.FAILED;
  const handleDelete = () =>
    confirm({
      title: t("company:payrolls.delete_run_title"),
      msg: t("company:payrolls.delete_run_desc"),
      color: "var(--color-danger)",
      confirmLabel: t("company:payrolls.delete_run"),
      onConfirm: async () => {
        try {
          await deleteRun(runId).unwrap();
          toast.success(t("company:payrolls.run_deleted"));
          navigate(ROUTES.HOME.COMPANY.PAYROLL.ROOT);
        } catch (err: any) {
          toast.error(
            err?.data?.message ?? t("company:payrolls.run_delete_failed"),
          );
        }
      },
    });

  // Approved but not processed: no longer deletable, still cancellable (B8).
  const canCancelRun = run?.status === PayrollRunStatusEnum.APPROVED;
  const [cancelOpen, setCancelOpen] = useState(false);

  // Employees whose pay failed to calculate: retry them, or exclude each
  // with a reason (B8). Retry runs on a draft only.
  const [retryFailed, { isLoading: isRetryingFailed }] =
    useRetryFailedMutation();
  const failedCount = Number(run?.failedJobs ?? 0);
  const handleRetryFailed = () =>
    toast
      .promise(retryFailed(runId).unwrap(), {
        loading: t("company:payrolls.retrying_failed"),
        success: t("company:payrolls.retry_started"),
        error: (err: any) =>
          err?.data?.message ?? t("company:payrolls.retry_failed"),
      })
      .then(() => refetchRun())
      .catch(() => undefined);

  const handlePrimary = useCallback(async () => {
    if (!runId || isNaN(runId)) return;
    const action =
      stage === "calculated"
        ? {
            promise: startReview(runId).unwrap(),
            loading: t("company:payrolls.starting_review"),
            success: t("company:payrolls.review_started"),
          }
        : stage === "in-review"
          ? {
              promise: approveRun(runId).unwrap(),
              loading: t("company:payrolls.approving_payroll"),
              success: t("company:payrolls.payroll_approved"),
            }
          : stage === "approved"
            ? {
                promise: processRun(runId)
                  .unwrap()
                  .then((res) => warnIfIbanChecksumFails(res, t)),
                loading: "Processing payroll — generating payment files...",
                success: "Processing started — files will appear once ready",
              }
            : null;
    if (!action) return;
    toast.promise(action.promise, {
      loading: action.loading,
      success: action.success,
      error: (err) => {
        const msg = err?.data?.message;
        return Array.isArray(msg)
          ? msg.join(", ")
          : (msg ?? err?.message ?? t("company:payrolls.action_failed"));
      },
    });
  }, [stage, runId, startReview, approveRun, processRun, t]);

  const periodLabel = run?.periodStart
    ? format(new Date(run.periodStart), "MMM yyyy")
    : "—";
  const breadcrumb = run
    ? [periodLabel, run.branchName].filter(Boolean).join(" — ") || "—"
    : id === "new"
      ? t("company:payrolls.new_run")
      : `Run #${id}`;

  const tableSubtitle = buildStageTableSubtitle(t)[stage];
  const actions = buildStageActions(t)[stage];

  if (isGenerating) {
    return (
      <div className="flex flex-col gap-4 bg-muted p-4 md:p-6">
        <Header
          title={
            run?.name
              ? displayRunName(t, run.name, localeDate)
              : t("company:payrolls.payroll_run")
          }
          breadcrumb={breadcrumb}
          onBack={handleBack}
          actions={buildStageActions(t).calculated}
          onPrimary={() => {}}
          isWorking={false}
        />
        <div className="rounded-xs border border-border bg-white px-5 py-5">
          <RunGenerationProgress progress={genProgress} items={genItems} />
        </div>
      </div>
    );
  }

  if (!run && !isNaN(runId)) {
    return (
      <div className="flex flex-col gap-4 bg-muted p-4 md:p-6 text-sm text-gray-400 py-8 text-center">
        {t("company:payrolls.loading_payroll_run")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 bg-muted p-4 md:p-6">
      <Header
        title={
          run?.name
            ? displayRunName(t, run.name, localeDate)
            : t("company:payrolls.payroll_run")
        }
        breadcrumb={breadcrumb}
        onBack={handleBack}
        actions={actions}
        onPrimary={handlePrimary}
        isWorking={isWorking}
      />

      <div className="rounded-xs border border-border bg-white px-5 py-4">
        <ProgressStepper steps={buildSteps(stage, t)} />
      </div>

      <SummaryAccordions
        totals={run?.totalsByCurrency ?? []}
        breakdown={run?.paymentMethodBreakdown ?? []}
      />

      {/* B37: not a block — an exempt employee says so on their compensation. */}
      {run?.outsideWpsWarning && (
        <div className="rounded-xs border border-warning/30 bg-warning-muted px-5 py-3 text-sm text-warning">
          {t("company:payrolls.outside_wps_warning", {
            count: run.outsideWpsWarning.count,
            amount: run.outsideWpsWarning.amount.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
            names: run.outsideWpsWarning.employees
              .map((e) => e.name)
              .join(", "),
          })}
        </div>
      )}

      {/* D4: a claim in another currency waits rather than being paid 1:1. */}
      {!!run?.reimbursementsWaiting?.length && (
        <div className="rounded-xs border border-warning/30 bg-warning-muted px-5 py-3 text-sm text-warning">
          {t("company:payrolls.reimbursements_waiting_warning", {
            count: run.reimbursementsWaiting.length,
            list: run.reimbursementsWaiting
              .map(
                (r) =>
                  `${r.employeeName} · ${r.currency} ${localeNumber(r.amount, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`,
              )
              .join(t("company:payslip.lop_separator")),
          })}
        </div>
      )}

      {/* D4: UAE law caps deductions at half the wage; a warning, not a block. */}
      {!!run?.deductionsOverHalf?.length && (
        <div className="rounded-xs border border-warning/30 bg-warning-muted px-5 py-3 text-sm text-warning">
          {t("company:payrolls.deductions_over_half_warning", {
            count: run.deductionsOverHalf.length,
            list: run.deductionsOverHalf
              .map((d) =>
                t("company:payrolls.deductions_over_half_entry", {
                  name: d.employeeName,
                  percent: localeNumber(d.percent, {
                    maximumFractionDigits: 1,
                  }),
                }),
              )
              .join(t("company:payslip.lop_separator")),
          })}
        </div>
      )}

      {failedCount > 0 && run?.status === PayrollRunStatusEnum.DRAFT && (
        <div className="flex items-center justify-between gap-4 rounded-xs border border-danger/30 bg-danger-muted px-5 py-3">
          <span className="text-sm text-danger">
            {t("company:payrolls.failed_employees_banner", {
              count: failedCount,
            })}
          </span>
          <PermissionButton
            section={PLATFORM_SECTION_ENUM.PAYROLL}
            action="edit"
            mode="disable"
            variant="outline-danger"
            size="sm"
            onClick={handleRetryFailed}
            disabled={isRetryingFailed}
          >
            {t("company:payrolls.retry_failed_button")}
          </PermissionButton>
        </div>
      )}

      <CancelRunDialog
        runId={runId}
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        onCancelled={() => navigate(ROUTES.HOME.COMPANY.PAYROLL.ROOT)}
      />

      {processFailed && (
        <ProcessFailedBanner
          error={processFailed.error}
          onRetry={handleRetryProcess}
          isRetrying={isProcessing}
        />
      )}

      {(processStarted || processFiles.length > 0) &&
        stage !== "processed" &&
        !processFailed && (
          <ProcessLiveCard
            started={processStarted}
            files={processFiles}
            isComplete={Boolean(
              processCompleted && processCompleted.status === "PROCESSING",
            )}
          />
        )}

      {stage === "processed" && (
        <div className="flex flex-col gap-4">
          <PaymentFilesCard
            runId={runId}
            files={paymentFiles}
            isLoading={isLoadingPaymentFiles}
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CashDisbursementCard
              runId={runId}
              items={cashDisbursements}
              isLoading={isLoadingCashDisbursements}
            />
            <BankTransferCard
              runId={runId}
              items={bankTransfers}
              isLoading={isLoadingBankTransfers}
            />
          </div>
        </div>
      )}

      {stage === "calculated" &&
        !otBannerDismissed &&
        otMismatchedItems.length > 0 && (
          <div className="flex items-start gap-3 rounded-xs border border-warning-muted bg-warning-muted/40 px-4 py-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <div className="flex flex-col gap-1 min-w-0 flex-1">
              <p className="text-xs text-warning">
                <Trans
                  t={t}
                  i18nKey="company:payrolls.ot_mismatch_banner"
                  count={otMismatchedItems.length}
                  components={{ b: <b /> }}
                />
              </p>
              <p className="text-[11px] text-warning/80 truncate">
                {otMismatchedItems
                  .slice(0, 3)
                  .map((i) => i.employee.fullName)
                  .join(", ")}
                {otMismatchedItems.length > 3
                  ? ` +${otMismatchedItems.length - 3} more`
                  : ""}
              </p>
            </div>
            <Button
              variant="outline-warning"
              size="sm"
              className="shrink-0"
              onClick={() => setOtBannerDismissed(true)}
            >
              {t("company:payrolls.reviewed_dismiss")}
            </Button>
          </div>
        )}

      {wpsExcludedCount > 0 && (
        <div
          className={
            wpsBlockingCount > 0
              ? "flex flex-col gap-3 rounded-xs border border-danger-muted bg-danger-muted/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              : "flex flex-col gap-3 rounded-xs border border-warning-muted bg-warning-muted/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
          }
        >
          <div className="flex items-center gap-2.5">
            <AlertTriangle
              className={
                wpsBlockingCount > 0
                  ? "h-4 w-4 shrink-0 text-danger"
                  : "h-4 w-4 shrink-0 text-warning"
              }
            />
            {wpsExcludedItems.length > 0 && (
              <div className="flex shrink-0 items-center -space-x-2">
                {wpsExcludedItems.slice(0, 3).map((emp, i) => (
                  <HoverPopover
                    key={emp.itemId}
                    contentClassName="flex w-auto items-center gap-2.5 p-3"
                    trigger={
                      <Avatar
                        style={{ zIndex: i + 1 }}
                        className="relative size-7 cursor-pointer bg-white ring-2 ring-white transition-transform hover:z-20 hover:scale-110"
                      >
                        {emp.profileImage && (
                          <AvatarImage
                            src={emp.profileImage}
                            alt={emp.name ?? ""}
                          />
                        )}
                        <AvatarFallback className="bg-primary/10 text-[10px] font-bold text-primary">
                          {getExcludedInitials(emp.name)}
                        </AvatarFallback>
                      </Avatar>
                    }
                  >
                    <Avatar className="size-9 shrink-0 bg-white">
                      {emp.profileImage && (
                        <AvatarImage
                          src={emp.profileImage}
                          alt={emp.name ?? ""}
                        />
                      )}
                      <AvatarFallback className="bg-primary/10 text-xs font-bold text-primary">
                        {getExcludedInitials(emp.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <span className="text-xs font-semibold text-foreground">
                        {emp.name ?? t("company:payrolls.unknown_employee")}
                      </span>
                      {emp.employeeCode && (
                        <span className="text-[11px] text-muted-foreground">
                          {emp.employeeCode}
                        </span>
                      )}
                      <span className="mt-0.5 text-[11px] font-medium text-danger">
                        {buildWpsExclusionLabels(t)[emp.reason] ?? emp.reason}
                      </span>
                    </div>
                  </HoverPopover>
                ))}
                {wpsExcludedCount > 3 && (
                  <HoverPopover
                    contentClassName="w-64 p-3"
                    trigger={
                      <button
                        type="button"
                        className="relative z-10 flex size-7 cursor-pointer items-center justify-center rounded-xs border-0 bg-white p-0 ring-2 ring-white transition-transform hover:z-20 hover:scale-110"
                      >
                        <span className="flex h-full w-full items-center justify-center rounded-xs bg-primary/10 text-[10px] font-bold text-primary">
                          +{wpsExcludedCount - 3}
                        </span>
                      </button>
                    }
                  >
                    <span className="text-[11px] font-semibold text-foreground">
                      {t("company:payrolls.n_more_excluded", {
                        count: wpsExcludedCount - 3,
                      })}
                    </span>
                    <div className="mt-1.5 flex max-h-64 flex-col gap-1.5 overflow-y-auto">
                      {wpsExcludedItems.slice(3).map((emp) => (
                        <div
                          key={emp.itemId}
                          className="flex items-center gap-2"
                        >
                          <Avatar className="size-6 shrink-0 bg-white">
                            {emp.profileImage && (
                              <AvatarImage
                                src={emp.profileImage}
                                alt={emp.name ?? ""}
                              />
                            )}
                            <AvatarFallback className="bg-primary/10 text-[9px] font-bold text-primary">
                              {getExcludedInitials(emp.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex min-w-0 flex-col">
                            <span className="truncate text-[11px] font-medium text-foreground">
                              {emp.name ??
                                t("company:payrolls.unknown_employee")}
                            </span>
                            <span className="truncate text-[10px] text-danger">
                              {buildWpsExclusionLabels(t)[emp.reason] ??
                                emp.reason}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </HoverPopover>
                )}
              </div>
            )}
            <p
              className={
                wpsBlockingCount > 0
                  ? "text-xs text-danger"
                  : "text-xs text-warning"
              }
            >
              <Trans
                i18nKey={
                  wpsBlockingCount > 0
                    ? "company:payrolls.wps_excluded_blocking"
                    : "company:payrolls.wps_excluded_not_blocking"
                }
                count={
                  wpsBlockingCount > 0 ? wpsBlockingCount : wpsExcludedCount
                }
                values={{
                  count:
                    wpsBlockingCount > 0 ? wpsBlockingCount : wpsExcludedCount,
                  excludedTab: t("company:payrolls.excluded"),
                  saveAndResolve: t("company:payrolls.save_and_resolve"),
                }}
                components={{
                  1: <b />,
                  2: <b />,
                  3: <b />,
                }}
              />
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setActiveTab("wps-excluded")}
            className="shrink-0 gap-1.5 border-danger/40 text-danger hover:bg-danger/10 hover:text-danger"
          >
            {t("company:payrolls.view_excluded")}
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      <div className="flex flex-col overflow-hidden rounded-xs border border-border bg-white">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex w-full h-auto items-stretch justify-start gap-0 rounded-none border-b border-border bg-transparent p-0">
            <TabsTrigger
              value="active"
              className="relative -mb-px flex shrink-0 items-center gap-2 rounded-none px-3 py-3 md:px-4 md:py-4 text-[10px] md:text-xs font-semibold uppercase tracking-wide text-primary-strong hover:bg-primary/5 hover:text-primary data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:border-b-[3px] data-[state=active]:border-primary"
            >
              {t("company:payrolls.active_count", {
                count: activeItemsCount,
              })}
            </TabsTrigger>
            <TabsTrigger
              value="hold"
              className="relative -mb-px flex shrink-0 items-center gap-2 rounded-none px-3 py-3 md:px-4 md:py-4 text-[10px] md:text-xs font-semibold uppercase tracking-wide text-primary-strong hover:bg-primary/5 hover:text-primary data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:border-b-[3px] data-[state=active]:border-primary"
            >
              {t("company:payrolls.on_hold_count", { count: heldItemsCount })}
            </TabsTrigger>
            <TabsTrigger
              value="wps-excluded"
              className="relative -mb-px flex shrink-0 items-center gap-2 rounded-none px-3 py-3 md:px-4 md:py-4 text-[10px] md:text-xs font-semibold uppercase tracking-wide text-primary-strong hover:bg-primary/5 hover:text-primary data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:border-b-[3px] data-[state=active]:border-primary"
            >
              {t("company:payrolls.excluded_count", {
                count: wpsExcludedCount,
              })}
            </TabsTrigger>
          </TabsList>
          <div className="px-5 py-4">
            <TabsContent value="active" className="mt-0">
              <EarningsTable
                runId={runId}
                subtitle={tableSubtitle}
                editable={stage === "in-review"}
                viewable={stage === "approved" || stage === "processed"}
                showMarkPaid={stage === "processed"}
                showReceiptSignatures={stage === "processed"}
                periodLabel={run?.periodLabel || periodLabel}
                excludeItemIds={blockingItemIds}
                signatureStarted={signatureStarted}
                signatureCompleted={signatureCompleted}
                signatureFailed={signatureFailed}
                approvedOtByUser={approvedOtByUser}
                otSyncStatus={otSyncStatus}
                allowOvertimeOverride={stage === "calculated"}
              />
            </TabsContent>
            <TabsContent value="hold" className="mt-0">
              <EarningsTable
                runId={runId}
                holdMode
                allowReprocess={stage === "approved" || stage === "processed"}
                subtitle={t(
                  "company:payrolls.held_salary_release_or_reprocess_hint",
                )}
                periodLabel={run?.periodLabel || periodLabel}
                approvedOtByUser={approvedOtByUser}
              />
            </TabsContent>
            <TabsContent value="wps-excluded" className="mt-0">
              <WpsExcludedTab
                runId={runId}
                canReinclude={stage === "in-review"}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>

      {stage !== "processed" && (
        <div className="flex items-center justify-between gap-4 rounded-xs border border-border bg-white px-5 py-4">
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-900">
              {actions.ctaTitle}
            </span>
            <span className="text-xs text-gray-400">
              {actions.ctaDescription}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" onClick={handleBack}>
              {actions.backLabel}
            </Button>
            {canDeleteRun && (
              <PermissionButton
                section={PLATFORM_SECTION_ENUM.PAYROLL}
                action="delete"
                mode="hide"
                variant="outline-danger"
                onClick={handleDelete}
                disabled={isWorking || isDeleting}
              >
                {isDeleting
                  ? t("company:payrolls.deleting")
                  : t("company:payrolls.delete_run")}
              </PermissionButton>
            )}
            {canCancelRun && (
              <PermissionButton
                section={PLATFORM_SECTION_ENUM.PAYROLL}
                action="delete"
                mode="hide"
                variant="outline-danger"
                onClick={() => setCancelOpen(true)}
                disabled={isWorking}
              >
                {t("company:payrolls.cancel_run")}
              </PermissionButton>
            )}
            <PermissionButton
              section={PLATFORM_SECTION_ENUM.PAYROLL}
              action="edit"
              mode="disable"
              variant="primary"
              onClick={handlePrimary}
              disabled={isWorking}
            >
              {isWorking ? t("common:states.working") : actions.primaryLabel}
            </PermissionButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollRun;
